// Workspace-scoped team queries + mutations. Used by TeamPage.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../../lib/api-client";

export interface TeamMember {
  id: string;
  email: string;
  name: string;
  role: "sales" | "manager" | "admin";
  isActive: boolean;
  lastLoginAt: string | null;
  deletedAt?: string | null;
}

interface ListResponse {
  users: TeamMember[];
}

export function useTeamMembers(
  workspaceId: string | undefined,
  opts: { includeDeleted?: boolean } = {}
) {
  const includeDeleted = !!opts.includeDeleted;
  return useQuery({
    enabled: !!workspaceId,
    queryKey: ["team", workspaceId, { includeDeleted }],
    queryFn: () =>
      apiClient.get<ListResponse>("/api/users", {
        params: {
          workspace: workspaceId,
          ...(includeDeleted ? { includeDeleted: "1" } : {}),
        },
      }),
  });
}

export function useInviteTeamMember(workspaceId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; role: "sales" | "manager" | "admin" }) =>
      apiClient.post<{ inviteId: string; acceptUrl: string }>(
        "/api/users/invite",
        { json: input }
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["team", workspaceId] });
      if (workspaceId) {
        void qc.invalidateQueries({
          queryKey: ["workspaces", workspaceId, "invites"],
        });
      }
    },
  });
}

export function useUpdateTeamMember(workspaceId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id: string;
      role?: "sales" | "manager" | "admin";
      isActive?: boolean;
      name?: string;
    }) => {
      const { id, ...patch } = input;
      return apiClient.patch<{ user: TeamMember }>(`/api/users/${id}`, {
        json: patch,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["team", workspaceId] });
    },
  });
}

export function useDeleteTeamMember(workspaceId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete<void>(`/api/users/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["team", workspaceId] });
    },
  });
}
